/**
 * LayoutView Component for UI-Model-1
 *
 * Layout structure (3-column with center stack):
 * - Component Library (left panel, full height) - only visible when a container is selected
 * - Center column: Model Header Preview + Navigation Preview + Canvas (stacked vertically)
 * - Config Panel (right panel, full height)
 *
 * This ensures Header and Navigation match Canvas width while sidebars span full height.
 *
 * Library Panel Visibility:
 * - Shows ComponentLibrary when no section (header/navigation) is selected
 * - Shows placeholder message when header, navigation, group, or page is selected
 *
 * Note: Submodel drag-and-drop is handled directly by Canvas, not through LayoutView.
 *
 * Drag-and-Drop:
 * - DragDropProvider wraps the entire layout to enable @hello-pangea/dnd
 * - Supports reordering items within containers
 * - Supports moving items between containers
 * - Supports dropping items from ComponentLibrary at specific positions
 */

import React, { useState } from 'react';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { DragDropProvider } from '../../contexts/DragDropContext';
import { CategoryAttributesCacheProvider } from '../../contexts/CategoryAttributesCacheContext';
import ModelHeaderPreview from '../ModelHeaderPreview';
import ModelFooterPreview from '../ModelFooterPreview';
import NavigationPreview from '../NavigationPreview';
import ComponentLibrary from '../ComponentLibrary';
import Canvas from '../Canvas';
import ConfigPanel from '../ConfigPanel';
// The old menu-toggle svg files baked a WHITE card into the artwork —
// theme-blind as <img>. Inline chevrons follow currentColor instead (the same
// fix the app sidebar's toggle got in okf/templates § 18).
const PanelChevrons = ({ flipped }: { flipped: boolean }) => (
  <svg
    viewBox="0 0 26 26"
    fill="none"
    aria-hidden="true"
    className={`tw-w-6 tw-aspect-square ${flipped ? 'tw-rotate-180' : ''}`}
  >
    <path d="M18 8.04761L13.0476 13L18 17.9524" stroke="currentColor" />
    <path d="M13.5237 8.04761L8.5713 13L13.5237 17.9524" stroke="currentColor" />
  </svg>
);

interface LayoutViewProps {
  id?: string;
}

export const LayoutView = ({ id }: LayoutViewProps) => {
  const baseId = id || 'layout-view';
  const { state } = useUIModelState();
  const { selectedSection, selectedGroupId, selectedPageId, selectedContainerId } = state.editorState;

  // Show Library only when a container is selected and no section/group/page is selected
  const showLibrary = selectedSection === null && selectedGroupId === null && selectedPageId === null && selectedContainerId !== null;

  const [showComponentLibrary, setShowComponentLibrary] = useState<boolean>(true);
  const [showConfigPanel, setShowConfigPanel] = useState<boolean>(true);

  return (
    <DragDropProvider>
      <CategoryAttributesCacheProvider>
        <div
          id={baseId}
          data-testid={baseId}
          className="tw-relative tw-flex tw-h-full tw-overflow-hidden"
        >
          {/* Left toggle button */}
          <button data-testid="layoutview-layoutview-button-1" aria-label="Toggle component library"
            id="sidebar-toggle-btn"
            className={`tw-absolute ${showComponentLibrary ? 'tw-left-[256px]' : 'tw-left-0'} tw-top-1 tw-transition-all tw-duration-100 tw-bg-surface tw-text-ink tw-border tw-border-edge tw-rounded hover:tw-bg-surface-hover tw-z-20 tw-p-0 tw-m-0 tw-h-min`}
            onClick={() => setShowComponentLibrary(prev => !prev)}
          >
            <PanelChevrons flipped={!showComponentLibrary} />
          </button>

          {/* Right toggle button */}
          <button data-testid="layoutview-layoutview-button-2" aria-label="Toggle config panel"
            id="config-panel-toggle-btn"
            className={`tw-absolute ${showConfigPanel ? 'tw-right-[320px]' : 'tw-right-0'} tw-top-1 tw-transition-all tw-duration-100 tw-bg-surface tw-text-ink tw-border tw-border-edge tw-rounded hover:tw-bg-surface-hover tw-z-20 tw-p-0 tw-m-0 tw-h-min`}
            onClick={() => setShowConfigPanel(prev => !prev)}
          >
            <PanelChevrons flipped={showConfigPanel} />
          </button>

          {/* Left: Component Library or placeholder (width-controlled wrapper) */}
          <div className={`${showComponentLibrary ? 'tw-w-64' : 'tw-w-0'} tw-flex-shrink-0 tw-overflow-hidden`}>
            {showLibrary ? (
              <ComponentLibrary id={`${baseId}-component-library`} />
            ) : (
              <div className="tw-w-64 tw-h-full tw-bg-surface tw-border-r tw-border-edge tw-flex tw-items-center tw-justify-center tw-p-4">
                <p className="tw-text-ink-secondary tw-text-sm tw-text-center">
                  Select a container to add components
                </p>
              </div>
            )}
          </div>

          {/* Center: Header + Navigation + Canvas (stacked vertically) */}
          <div
            id={`${baseId}-center-column`}
            className="tw-flex tw-flex-col tw-flex-1 tw-min-w-0 tw-overflow-hidden"
          >
            {/* Model Header Preview - matches canvas width */}
            <ModelHeaderPreview id={`${baseId}-header-preview`} />

            {/* Navigation Preview - matches canvas width */}
            <NavigationPreview id={`${baseId}-nav-preview`} />

            {/* Canvas - takes remaining height */}
            <Canvas id={`${baseId}-canvas`} />

            {/* Footer Preview - below canvas */}
            <ModelFooterPreview id={`${baseId}-footer-preview`} />
          </div>

          {/* Right: Config Panel (width-controlled wrapper) */}
          <div className={`${showConfigPanel ? 'tw-w-80' : 'tw-w-0'} tw-flex-shrink-0 tw-overflow-hidden`}>
            <ConfigPanel id={`${baseId}-config-panel`} />
          </div>
        </div>
      </CategoryAttributesCacheProvider>
    </DragDropProvider>
  );
};

export default LayoutView;
